// src/components/AnalyticsPanel.jsx
import React, { useMemo, memo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Select,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Tooltip,
  TextField,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import {
  filterAnalyticsRows,
  getNewTrancheColumns,
  hasOnlyNewTranches,
  isTextMetric,
  NEW_CUSIP_NOTICE,
  NEW_TRANCHE_LABEL,
  orderAnalyticsRows,
} from '../utils/analyticsRows';

// Self-contained Price Calculator with local state for instant responsiveness
// State is kept fully local - only synced to parent on Generate click
const PriceCalculator = memo(({
  visibleTranches,
  priceTrancheTargets,
  onPriceTrancheTargetsChange,
  yieldTrancheTargets,
  onYieldTrancheTargetsChange,
  spreadTrancheTargets,
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets,
  onOwnPctTrancheTargetsChange,
  equityTranches,
  priceData,
  onGenerate,
  generationLoading,
  generateDisabled,
  hasGeneratedData,
  generationError,
  shockBps,
  onShockBpsChange,
  adcoApplied,
  theme
}) => {
  // Local state for immediate UI updates - fully controlled locally
  const [localPrices, setLocalPrices] = useState({});
  const [localYields, setLocalYields] = useState({});
  const [localSpreads, setLocalSpreads] = useState({});
  const [localOwnPcts, setLocalOwnPcts] = useState({});
  const [localShockBps, setLocalShockBps] = useState('');
  const initializedRef = useRef(false);
  
  // Only sync from external state on initial mount or when cleared (all empty)
  useEffect(() => {
    const allEmpty = !priceTrancheTargets || Object.keys(priceTrancheTargets).length === 0 ||
      Object.values(priceTrancheTargets).every(v => v === '' || v === null || v === undefined);
    
    if (!initializedRef.current || allEmpty) {
      setLocalPrices(priceTrancheTargets || {});
      initializedRef.current = true;
    }
  }, [priceTrancheTargets]);
  
  useEffect(() => {
    const allEmpty = !yieldTrancheTargets || Object.keys(yieldTrancheTargets).length === 0 ||
      Object.values(yieldTrancheTargets).every(v => v === '' || v === null || v === undefined);
    
    if (allEmpty) {
      setLocalYields(yieldTrancheTargets || {});
    }
  }, [yieldTrancheTargets]);
  
  useEffect(() => {
    const allEmpty = !spreadTrancheTargets || Object.keys(spreadTrancheTargets).length === 0 ||
      Object.values(spreadTrancheTargets).every(v => v === '' || v === null || v === undefined);

    if (allEmpty) {
      setLocalSpreads(spreadTrancheTargets || {});
    }
  }, [spreadTrancheTargets]);

  useEffect(() => {
    const allEmpty = !ownPctTrancheTargets || Object.keys(ownPctTrancheTargets).length === 0 ||
      Object.values(ownPctTrancheTargets).every(v => v === '' || v === null || v === undefined);
    if (allEmpty) {
      setLocalOwnPcts(ownPctTrancheTargets || {});
    }
  }, [ownPctTrancheTargets]);

  // Sync shock bps from parent when cleared
  useEffect(() => {
    if (shockBps === '' || shockBps === null || shockBps === undefined) {
      setLocalShockBps('');
    }
  }, [shockBps]);

  // Helper to check if a value exists
  const hasValue = (val) => val !== '' && val !== null && val !== undefined;
  
  // Yield and spread are mutually exclusive; price is always editable
  const isYieldDisabled = (tranche) => hasValue(localSpreads[tranche]);
  const isSpreadDisabled = (tranche) => hasValue(localYields[tranche]);
  
  // Simple handler - just update local state, no parent sync during editing
  const handleInputChange = useCallback((setter, tranche, value) => {
    const numValue = value === '' ? '' : Number(value);
    setter(prev => ({ ...prev, [tranche]: numValue }));
  }, []);
  
  // Sync all local state to parent and trigger generate
  const handleGenerateClick = useCallback(() => {
    if (onPriceTrancheTargetsChange) onPriceTrancheTargetsChange(localPrices);
    if (onYieldTrancheTargetsChange) onYieldTrancheTargetsChange(localYields);
    if (onSpreadTrancheTargetsChange) onSpreadTrancheTargetsChange(localSpreads);
    if (onOwnPctTrancheTargetsChange) onOwnPctTrancheTargetsChange(localOwnPcts);
    if (onShockBpsChange) onShockBpsChange(localShockBps);

    setTimeout(() => {
      if (onGenerate) onGenerate();
    }, 50);
  }, [localPrices, localYields, localSpreads, localOwnPcts, localShockBps, onPriceTrancheTargetsChange, onYieldTrancheTargetsChange, onSpreadTrancheTargetsChange, onOwnPctTrancheTargetsChange, onShockBpsChange, onGenerate]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.5)
            : '#f8fafc',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: '13px',
            color: theme.palette.text.primary
          }}
        >
          Price Calculator
        </Typography>

        {adcoApplied && (
          <Tooltip title="This security has stored ADCO curves; they are used as the CPR/CDR/SEV assumptions for Generate. Re-run ADCO to refresh them.">
            <Chip
              label="ADCO curves applied"
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 20, fontSize: '10px', mr: 'auto', ml: 1 }}
            />
          </Tooltip>
        )}

        {onGenerate && (
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateClick}
            disabled={generateDisabled || generationLoading}
            sx={{
              bgcolor: generationLoading 
                ? theme.palette.action.disabled 
                : theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontSize: '12px',
              borderRadius: 1,
              minHeight: 28,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled
              }
            }}
          >
            {generationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} sx={{ color: 'white' }} />
                Generating...
              </Box>
            ) : hasGeneratedData ? (
              'Re-Generate'
            ) : (
              'Generate'
            )}
          </Button>
        )}
      </Box>

      {/* Input Grid */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `200px repeat(${visibleTranches.length}, 1fr)`,
            gap: 1,
            alignItems: 'center'
          }}
        >
          {/* Tranche Header Row */}
          <Box />
          {visibleTranches.map(tranche => (
            <Typography
              key={`header-${tranche}`}
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                textAlign: 'center'
              }}
            >
              {tranche}
            </Typography>
          ))}

          {/* Price Row */}
          <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
            Price
          </Typography>
          {visibleTranches.map(tranche => {
            return (
              <TextField
                key={`price-${tranche}`}
                fullWidth
                placeholder="100.00"
                type="number"
                size="small"
                value={localPrices[tranche] ?? ''}
                onChange={(e) => handleInputChange(setLocalPrices, tranche, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    bgcolor: theme.palette.background.paper,
                    fontSize: '13px',
                    '& input': {
                      textAlign: 'center',
                      py: 0.75
                    },
                    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                      opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                    }
                  }
                }}
              />
            );
          })}

          {/* Yield Row */}
          <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
            Yield %
          </Typography>
          {visibleTranches.map(tranche => {
            const isDisabled = isYieldDisabled(tranche);
            return (
              <TextField
                key={`yield-${tranche}`}
                fullWidth
                placeholder="5.00"
                type="number"
                size="small"
                disabled={isDisabled}
                value={localYields[tranche] ?? ''}
                onChange={(e) => handleInputChange(setLocalYields, tranche, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    bgcolor: isDisabled 
                      ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
                      : theme.palette.background.paper,
                    fontSize: '13px',
                    '& input': {
                      textAlign: 'center',
                      py: 0.75
                    },
                    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                      opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                    },
                    '&.Mui-disabled': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5'
                    }
                  }
                }}
              />
            );
          })}

          {/* Spread Row */}
          <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
            Spread (bps)
          </Typography>
          {visibleTranches.map(tranche => {
            const isDisabled = isSpreadDisabled(tranche);
            return (
              <TextField
                key={`spread-${tranche}`}
                fullWidth
                placeholder="500"
                type="number"
                size="small"
                disabled={isDisabled}
                value={localSpreads[tranche] ?? ''}
                onChange={(e) => handleInputChange(setLocalSpreads, tranche, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    bgcolor: isDisabled 
                      ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
                      : theme.palette.background.paper,
                    fontSize: '13px',
                    '& input': {
                      textAlign: 'center',
                      py: 0.75
                    },
                    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                      opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                    },
                    '&.Mui-disabled': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5'
                    }
                  }
                }}
              />
            );
          })}

          {/* Own % Row — only shown when at least one visible tranche is equity */}
          {visibleTranches.some(t => equityTranches[t]) && (
            <>
              <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
                Own %
              </Typography>
              {visibleTranches.map(tranche => (
                equityTranches[tranche] ? (
                  <TextField
                    key={`own-${tranche}`}
                    fullWidth
                    placeholder="100.00"
                    type="number"
                    size="small"
                    value={localOwnPcts[tranche] ?? ''}
                    onChange={(e) => handleInputChange(setLocalOwnPcts, tranche, e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        bgcolor: theme.palette.background.paper,
                        fontSize: '13px',
                        '& input': {
                          textAlign: 'center',
                          py: 0.75
                        },
                        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                          opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                          filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                        }
                      }
                    }}
                  />
                ) : (
                  <Box key={`own-${tranche}`} />
                )
              ))}
            </>
          )}

          {/* Shock (bps) Row - single input spanning all tranche columns */}
          <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
            Shock (bps)
          </Typography>
          <Box sx={{ gridColumn: `span ${visibleTranches.length}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="e.g. 50"
              type="number"
              size="small"
              value={localShockBps}
              onChange={(e) => setLocalShockBps(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{
                maxWidth: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  bgcolor: localShockBps ? alpha('#ff9800', 0.08) : theme.palette.background.paper,
                  fontSize: '13px',
                  '& input': {
                    textAlign: 'center',
                    py: 0.75
                  },
                  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                    opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                  }
                }
              }}
            />
            {localShockBps > 0 && (
              <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                Will run +{localShockBps}, Base, -{localShockBps} scenarios
              </Typography>
            )}
          </Box>

          {/* Price Results Row */}
          {priceData && priceData.length > 0 && priceData.some(result => result.price !== null && result.price !== undefined) && (
            <>
              <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>
                Calc. Price
              </Typography>
              {visibleTranches.map(tranche => {
                const priceResult = priceData.find(p => p.tranche === tranche);
                return (
                  <TextField
                    key={`result-${tranche}`}
                    fullWidth
                    size="small"
                    value={priceResult ? `${priceResult.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--'}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : '#f9fafb',
                        fontSize: '13px',
                        fontWeight: 600,
                        '& input': {
                          textAlign: 'center',
                          py: 0.75,
                          cursor: 'default'
                        }
                      }
                    }}
                  />
                );
              })}
            </>
          )}
        </Box>

        {/* Generation Error Display */}
        {generationError && (
          <TextField
            value={generationError}
            multiline
            rows={2}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: alpha(theme.palette.error.main, 0.08),
                borderColor: theme.palette.error.main,
                '& fieldset': { borderColor: theme.palette.error.main },
                '& textarea': { fontSize: '13px', color: theme.palette.error.main }
              }
            }}
          />
        )}
      </Box>
    </Paper>
  );
});

PriceCalculator.displayName = 'PriceCalculator';

const AnalyticsPanel = memo(({
  analyticsData,
  tranches,               // array of all tranches sent to API
  selectedTranches,       // array of tranche keys to show
  onTranchesChange,       // handler to pick multiple
  loading = false,
  dealName = '',          // current deal name for display
  // Price calculator props
  priceTrancheTargets = {},
  onPriceTrancheTargetsChange,
  yieldTrancheTargets = {},
  onYieldTrancheTargetsChange,
  spreadTrancheTargets = {},
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets = {},
  onOwnPctTrancheTargetsChange,
  equityTranches = {},
  priceData = [],
  // Shock comparison props
  shockBps = '',
  adcoApplied = false,
  onShockBpsChange,
  // Generate action props
  onGenerate,
  generationLoading = false,
  generateDisabled = false,
  hasGeneratedData = false,
  generationError = ''
}) => {
  const theme = useTheme();
  const gridRef = useRef(null);

  // Parse the data if it's a string
  const parsedData = useMemo(() => {
    if (typeof analyticsData === 'string') {
      try {
        // Replace NaN with null to make valid JSON (handle various formats)
        const cleanedData = analyticsData.replace(/:\s*NaN(?=\s*[,}])/g, ': null');
        const parsed = JSON.parse(cleanedData);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('AnalyticsPanel - JSON parse error:', e);
        return [];
      }
    }

    // Ensure we always return an array
    if (Array.isArray(analyticsData)) {
      return analyticsData;
    }

    // If it's an object with data property, use that
    if (analyticsData && typeof analyticsData === 'object' && analyticsData.data) {
      return Array.isArray(analyticsData.data) ? analyticsData.data : [];
    }

    return [];
  }, [analyticsData]);

  // The data is pivoted (metrics as rows, tranches as columns). These are the
  // tranche columns actually on screen, narrowed by the tranche selector the
  // same way colDefs narrows them.
  const displayedTrancheFields = useMemo(() => {
    const fields = Object.keys(parsedData?.[0] || {});
    return fields.filter((field) => {
      if (field === 'Metric' || !field.includes('Tranche') || !field.includes(':')) return false;
      if (!selectedTranches?.length) return true;
      return selectedTranches.includes(field.split(':')[0].replace('Tranche ', '').trim());
    });
  }, [parsedData, selectedTranches]);

  // A tranche with no book value is a CUSIP we do not hold.
  const newTrancheFields = useMemo(
    () => getNewTrancheColumns(parsedData, { columns: displayedTrancheFields }),
    [parsedData, displayedTrancheFields]
  );

  const positionRowsHidden = useMemo(
    () => hasOnlyNewTranches(parsedData, { columns: displayedTrancheFields }),
    [parsedData, displayedTrancheFields]
  );

  const filteredData = useMemo(
    () => orderAnalyticsRows(filterAnalyticsRows(parsedData, { columns: displayedTrancheFields })),
    [parsedData, displayedTrancheFields]
  );

  // Calculate dynamic height based on data
  const calculateTableHeight = useMemo(() => {
    const rowHeight = 32;
    const headerHeight = 36;
    const controlsHeight = 60; // Space for title and dropdown controls
    const padding = 20;
    const minHeight = 400; // Increased minimum height
    const maxHeight = 800; // Increased maximum height

    const dataRows = filteredData?.length || 0;
    const calculatedHeight = headerHeight + (dataRows * rowHeight) + controlsHeight + padding;

    return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  }, [filteredData]);

  const colDefs = useMemo(() => {
    if (!parsedData?.length) {
      return [{ headerName: 'No Analytics Data', field: 'noData', flex: 1, sortable: false, filter: false }];
    }

    // Get all column fields from first row
    const firstItem = parsedData[0];
    const allFields = Object.keys(firstItem || {});

    // Define the columns to display - exclude technical fields and focus on key analytics
    const excludeFields = ['RUN_DATE', 'Metric', 'Acct_ID', 'Lot_ID'];
    const displayFields = [
      'CUSIP',
      'Deal',
      'Tranche',
      'BOOK_VALUE',
      'WAL',
      'Book_Price',
      'Book_Yield',
      'Market_Price',
      'Market_Yield',
      'Spread_BP',
      'Z_spread_BP',
      'Mod_Duration_BP',
      'Eff_Duration_BP',
      'MOD_Convexity_BP',
      'Spread_MP',
      'Z_spread_MP',
      'Mod_Duration_MP',
      'Eff_Duration_MP',
      'Mod_Convexity_MP',
      'STAT_PRICE',
      'STAT_YIELD',
      'SPREAD_SP',
      'Z_spread_SP',
      'DISC_MARGIN_SP',
      'GAAP_PRICE',
      'GAAP_YIELD',
      'SPREAD_GP',
      'Z_spread_GP',
      'DISC_MARGIN_GP',
      'PRINCIPAL_WRITEDOWN',
      'PRINCIPAL_WINDOW',
      'CASHFLOW_WINDOW',
      'NET_ASSET_LOSS',
      'NET_ASSET_LOSS_HISTORY'
    ];

    const cols = [];

    // Handle tranche-prefixed format (e.g., "Tranche D: 03331PAG3")
    if (allFields.some(field => field.includes('Tranche'))) {

      // Always include the Metric column first
      if (allFields.includes('Metric')) {
        cols.push({
          headerName: 'Metric',
          field: 'Metric',
          pinned: 'left',
          width: 200,
          sortable: true,
          filter: true,
          cellStyle: { fontWeight: 'bold' }
        });
      }

      // Filter and show only selected tranche fields
      allFields.forEach(field => {
        if (field !== 'Metric') { // Skip the Metric field as it's already added
          // Check if this field belongs to a selected tranche
          let shouldInclude = true;
          if (selectedTranches?.length > 0 && field.includes('Tranche') && field.includes(':')) {
            const parts = field.split(':');
            if (parts.length >= 2) {
              const tranchePart = parts[0].replace('Tranche ', '').trim();
              shouldInclude = selectedTranches.includes(tranchePart);
            }
          }
          // If no tranches are selected, show all tranche fields
          else if (selectedTranches?.length === 0 && field.includes('Tranche')) {
            shouldInclude = true;
          }

          if (shouldInclude) {
            // Create readable header names
            let headerName = field;
            if (field.includes('Tranche') && field.includes(':')) {
              // Format: "Tranche D: 03331PAG3" -> "D (03331PAG3)"
              const parts = field.split(':');
              if (parts.length >= 2) {
                const tranchePart = parts[0].replace('Tranche ', '').trim();
                const cusipPart = parts[1].trim();
                headerName = `${tranchePart} (${cusipPart})`;
              }
            }

            // Mark the tranches we hold nothing of.
            if (newTrancheFields.has(field)) {
              headerName = `${headerName} · ${NEW_TRANCHE_LABEL}`;
            }

            const isDateColumn = field === 'ASOFDT' || field.toLowerCase().includes('date');
            cols.push({
              headerName: headerName,
              field: field,
              flex: 1,
              sortable: true,
              filter: isDateColumn ? 'agDateColumnFilter' : true,
              ...(isDateColumn && {
                filterParams: {
                  comparator: (filterDate, cellValue) => {
                    if (!cellValue) return -1;

                    let cellDate;

                    if (cellValue instanceof Date) {
                      cellDate = new Date(cellValue);
                    } else if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                      cellDate = new Date(cellValue);
                    } else if (typeof cellValue === 'number' || (typeof cellValue === 'string' && cellValue.match(/^\d{8}$/))) {
                      const str = String(cellValue);
                      if (str.length === 8) {
                        const year = parseInt(str.slice(0, 4));
                        const month = parseInt(str.slice(4, 6)) - 1;
                        const day = parseInt(str.slice(6, 8));
                        cellDate = new Date(year, month, day);
                      }
                    } else if (typeof cellValue === 'string') {
                      cellDate = new Date(cellValue);
                    }

                    if (!cellDate || isNaN(cellDate.getTime())) return 0;

                    cellDate.setHours(0, 0, 0, 0);
                    filterDate.setHours(0, 0, 0, 0);

                    if (cellDate < filterDate) return -1;
                    if (cellDate > filterDate) return 1;
                    return 0;
                  }
                }
              }),
              minWidth: 120,
              valueFormatter: ({ value, data }) => {
                if (typeof value === 'string' && isTextMetric(data?.Metric)) return value;
                if (value === null || value === undefined || isNaN(value)) return '--';
                if (typeof value === 'number') {
                  // Format YEAR column as plain integer without commas
                  if (field.toLowerCase().includes('year')) {
                    return Math.round(value).toString();
                  }
                  // Format other numeric columns with 2 decimal places and commas
                  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }
                return value;
              }
            });
          }
        }
      });
    } else {
      // Original flat format - create columns for available fields
      displayFields.forEach(field => {
        if (allFields.includes(field)) {
          const isDateColumn = field === 'ASOFDT' || field.toLowerCase().includes('date');
          cols.push({
            headerName: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            field: field,
            flex: 1,
            sortable: true,
            filter: isDateColumn ? 'agDateColumnFilter' : true,
            ...(isDateColumn && {
              filterParams: {
                comparator: (filterDate, cellValue) => {
                  if (!cellValue) return -1;

                  let cellDate;

                  if (cellValue instanceof Date) {
                    cellDate = new Date(cellValue);
                  } else if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                    cellDate = new Date(cellValue);
                  } else if (typeof cellValue === 'number' || (typeof cellValue === 'string' && cellValue.match(/^\d{8}$/))) {
                    const str = String(cellValue);
                    if (str.length === 8) {
                      const year = parseInt(str.slice(0, 4));
                      const month = parseInt(str.slice(4, 6)) - 1;
                      const day = parseInt(str.slice(6, 8));
                      cellDate = new Date(year, month, day);
                    }
                  } else if (typeof cellValue === 'string') {
                    cellDate = new Date(cellValue);
                  }

                  if (!cellDate || isNaN(cellDate.getTime())) return 0;

                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);

                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                }
              }
            }),
            minWidth: 120,
            valueFormatter: ({ value }) => {
              if (value === null || value === undefined || isNaN(value)) return '--';
              if (typeof value === 'number') {
                // Format based on field type with commas
                if (field.includes('PRICE') || field.includes('VALUE') || field.includes('WRITEDOWN') || field.includes('LOSS')) {
                  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                } else if (field.includes('YIELD') || field.includes('SPREAD') || field.includes('DURATION') || field.includes('CONVEXITY')) {
                  return value.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                } else if (field.includes('BP') || field.includes('MP') || field.includes('SP') || field.includes('GP')) {
                  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                } else {
                  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }
              }
              return value;
            }
          });
        }
      });
    }

    // If no columns were created, add a fallback
    if (cols.length === 0) {
      cols.push({
        headerName: 'No Matching Fields',
        field: 'noFields',
        flex: 1,
        sortable: false,
        filter: false
      });
    }

    return cols;
  }, [parsedData, selectedTranches, tranches, newTrancheFields]);

  // Extract visible tranche names for price calculator alignment
  const visibleTranches = useMemo(() => {
    if (!parsedData?.length) return [];

    const firstItem = parsedData[0];
    const allFields = Object.keys(firstItem || {});
    const trancheNames = [];

    // Handle tranche-prefixed format (e.g., "Tranche D: 03331PAG3")
    if (allFields.some(field => field.includes('Tranche'))) {
      allFields.forEach(field => {
        if (field !== 'Metric' && field.includes('Tranche') && field.includes(':')) {
          // Check if this field belongs to a selected tranche
          let shouldInclude = true;
          if (selectedTranches?.length > 0) {
            const parts = field.split(':');
            if (parts.length >= 2) {
              const tranchePart = parts[0].replace('Tranche ', '').trim();
              shouldInclude = selectedTranches.includes(tranchePart);
            }
          }

          if (shouldInclude) {
            const parts = field.split(':');
            if (parts.length >= 2) {
              const tranchePart = parts[0].replace('Tranche ', '').trim();
              if (!trancheNames.includes(tranchePart)) {
                trancheNames.push(tranchePart);
              }
            }
          }
        }
      });
    }

    return trancheNames;
  }, [parsedData, selectedTranches]);

  // Always show the panel - don't return null
  // if (!parsedData?.length && !selectedTranches?.length) return null;

  // Show empty grid with column headers if tranches selected but no data yet
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Professional Controls Header */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} sx={{ position: 'relative' }}>
          {/* Left - Select Tranches */}
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl
              size="small"
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                  }
                }
              }}
            >
              <InputLabel sx={{ fontWeight: 500 }}>Select Tranches</InputLabel>
              <Select
                multiple
                value={selectedTranches}
                onChange={(e) => onTranchesChange(e.target.value)}
                label="Select Tranches"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.slice(0, 2).map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '11px',
                          bgcolor: alpha(theme.palette.secondary.main, 0.15),
                          color: theme.palette.secondary.dark,
                          fontWeight: 600
                        }}
                      />
                    ))}
                    {selected.length > 2 && (
                      <Chip
                        label={`+${selected.length - 2} more`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '11px',
                          bgcolor: alpha(theme.palette.text.primary, 0.08),
                          color: theme.palette.text.secondary
                        }}
                      />
                    )}
                  </Box>
                )}
              >
                {tranches.map((tranche) => (
                  <MenuItem key={tranche} value={tranche}>
                    {tranche}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Center - Deal Name */}
          {dealName && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: theme.palette.text.primary,
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            >
              {dealName}
            </Typography>
          )}

          {/* Right - Export */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!parsedData || parsedData.length === 0}
            onClick={() => {
              if (gridRef.current?.api) {
                gridRef.current.api.exportDataAsExcel({
                  fileName: 'analytics.xlsx',
                  sheetName: 'Analytics',
                });
              }
            }}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 1,
              textTransform: 'none',
              fontSize: '13px',
              transition: 'all 0.2s ease'
            }}
          >
            Export
          </Button>
        </Box>
      </Box>
      {positionRowsHidden && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {NEW_CUSIP_NOTICE}
        </Typography>
      )}
      <Box sx={{ height: `${calculateTableHeight}px`, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <PamGrid
          ref={gridRef}
          rowData={filteredData}
          columnDefs={colDefs}
          defaultColDef={{
            flex: 1,
            sortable: true,
            filter: true,
            resizable: true,
            editable: false,
          }}
          height="100%"
          containerSx={{ height: '100%' }}
          gridContainerSx={{ height: '100%' }}
          showQuickFilter={false}
          showFooter={false}
          showToolbar={false}
          showExport={false}
          rowHeight={36}
          headerHeight={42}
          suppressHorizontalScroll={false}
          animateRows={false}
          suppressMovableColumns={false}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
        />
      </Box>

      {/* Generate Button - Standalone when no Price Calculator visible */}
      {visibleTranches.length === 0 && onGenerate && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            onClick={onGenerate}
            disabled={generateDisabled || generationLoading}
            sx={{
              bgcolor: generationLoading 
                ? theme.palette.action.disabled 
                : theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontSize: '12px',
              borderRadius: 1,
              minHeight: 28,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled
              }
            }}
          >
            {generationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} sx={{ color: 'white' }} />
                Generating...
              </Box>
            ) : (
              'Generate'
            )}
          </Button>
        </Box>
      )}

      {/* Generation Error Display - Standalone */}
      {visibleTranches.length === 0 && generationError && (
        <TextField
          value={generationError}
          multiline
          rows={2}
          fullWidth
          InputProps={{
            readOnly: true,
          }}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: alpha(theme.palette.error.main, 0.08),
              borderColor: theme.palette.error.main,
              '& fieldset': {
                borderColor: theme.palette.error.main,
              },
              '& textarea': {
                fontSize: '13px',
                color: theme.palette.error.main,
              }
            }
          }}
        />
      )}

      {/* Price Calculator Section - Self-contained component with local state for instant responsiveness */}
      {visibleTranches.length > 0 && (
        <PriceCalculator
          visibleTranches={visibleTranches}
          priceTrancheTargets={priceTrancheTargets}
          onPriceTrancheTargetsChange={onPriceTrancheTargetsChange}
          yieldTrancheTargets={yieldTrancheTargets}
          onYieldTrancheTargetsChange={onYieldTrancheTargetsChange}
          spreadTrancheTargets={spreadTrancheTargets}
          onSpreadTrancheTargetsChange={onSpreadTrancheTargetsChange}
          ownPctTrancheTargets={ownPctTrancheTargets}
          onOwnPctTrancheTargetsChange={onOwnPctTrancheTargetsChange}
          equityTranches={equityTranches}
          priceData={priceData}
          shockBps={shockBps}
          onShockBpsChange={onShockBpsChange}
          onGenerate={onGenerate}
          generationLoading={generationLoading}
          generateDisabled={generateDisabled}
          hasGeneratedData={hasGeneratedData}
          generationError={generationError}
          adcoApplied={adcoApplied}
          theme={theme}
        />
      )}

    </Box>
  );
});

AnalyticsPanel.displayName = 'AnalyticsPanel';

export default AnalyticsPanel;
